import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const STATS = [
  { value: "450", suffix: "", label: "Inspection Points", icon: "ri-checkbox-circle-line" },
  { value: "25", suffix: "+", label: "Years Experience", icon: "ri-award-line" },
  { value: "$139", suffix: "", label: "Starting Price", icon: "ri-price-tag-3-line" },
  { value: "NY·NJ·CT", suffix: "", label: "Tri-State Coverage", icon: "ri-map-pin-2-line" },
];

export default function HeroSection() {
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("lp-visible");
        });
      },
      { threshold: 0.15 }
    );
    const els = document.querySelectorAll(".lp-reveal");
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Cinematic Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/5421775f010302293f75dd9dc4a6b943.jpg"
          alt="Professional 450-point pre-purchase used car inspection — Lemon Protector"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer dark overlays for legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-black/95 via-brand-black/80 to-brand-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black/50 via-transparent to-brand-black/90" />
        {/* Subtle gold atmospheric tint */}
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/4 via-transparent to-transparent" />
      </div>

      {/* Animated vertical accent lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-px bg-gradient-to-b from-transparent via-brand-gold/20 to-transparent"
            style={{
              left: `${10 + i * 18}%`,
              height: "55%",
              top: `${10 + (i % 3) * 10}%`,
              animation: `lp-float ${4.5 + i * 0.7}s ease-in-out infinite alternate`,
            }}
          />
        ))}
      </div>

      {/* Corner decorative brackets */}
      <div className="absolute top-28 left-8 lg:left-14 w-12 h-12 border-l-2 border-t-2 border-brand-gold/25 rounded-tl-lg pointer-events-none" />
      <div className="absolute bottom-16 right-8 lg:right-14 w-12 h-12 border-r-2 border-b-2 border-brand-gold/25 rounded-br-lg pointer-events-none" />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24">
        <div className="max-w-[700px]">

          {/* Overline badge */}
          <div className="lp-reveal mb-8 inline-flex items-center gap-3 px-4 py-2 border border-brand-gold/30 rounded-full bg-brand-gold/8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-brand-gold animate-pulse" />
            <span className="font-sans text-xs tracking-widest uppercase text-brand-gold font-medium">
              Premier Used Car Inspections — NY · NJ · CT
            </span>
          </div>

          {/* H1 Headline */}
          <h1 className="lp-reveal lp-reveal-delay-1 font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-brand-white leading-[1.05] mb-6">
            Don&apos;t Buy a{" "}
            <span className="lp-shimmer-text italic">Lemon.</span>
            <br />
            <span className="text-brand-white">Know Before</span>{" "}
            <span className="text-brand-gold italic">You Buy.</span>
          </h1>

          {/* Subheadline */}
          <p className="lp-reveal lp-reveal-delay-2 font-sans text-lg md:text-xl text-brand-gray-light leading-relaxed max-w-2xl mb-5">
            Professional <strong className="text-brand-white font-semibold">450-point pre-purchase inspection</strong> — we travel on-site to the vehicle, and you speak <em>directly</em> to the actual inspector.
          </p>

          {/* Pricing & feature pills */}
          <div className="lp-reveal lp-reveal-delay-2 flex flex-wrap items-center gap-3 mb-10">
            <span className="font-serif text-2xl font-bold text-brand-gold">Starting at $139</span>
            <span className="w-px h-6 bg-brand-gold/30" />
            <div className="flex flex-wrap gap-2">
              {["On-Site Mobile Service", "Free Title & History Report", "Inspector Calls You Direct"].map((pill) => (
                <span key={pill} className="px-3 py-1 rounded-full border border-brand-gold/25 bg-brand-gold/6 font-sans text-xs text-brand-gray-light whitespace-nowrap">
                  {pill}
                </span>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="lp-reveal lp-reveal-delay-3 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2.5 px-9 py-4.5 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg shadow-brand-gold/20"
            >
              <i className="ri-search-line text-base" />
              Schedule a 450-Point Inspection
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:5169833800"
              className="inline-flex items-center justify-center gap-2.5 px-9 py-4.5 border border-brand-gold/50 text-brand-gold font-sans font-medium text-sm tracking-wide rounded-full hover:bg-brand-gold/10 hover:border-brand-gold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-base" />
              Call Now: (516) 983-3800
            </a>
          </div>

          {/* Trust badges row */}
          <div className="lp-reveal lp-reveal-delay-4 mt-8 flex flex-wrap gap-5 items-center">
            <div className="flex items-center gap-2">
              <img
                src="https://lemonprotector.com/images/home/5stars.gif"
                alt="5-star reviews"
                className="h-5 object-contain"
              />
              <span className="font-sans text-xs text-brand-gray">Verified Reviews</span>
            </div>
            <span className="w-px h-4 bg-brand-gold/20" />
            <a
              href="http://www.bbb.org/new-york-city/business-reviews/auto-diagnostic-service/lemon-protector-in-plainview-ny-8459/"
              rel="nofollow"
              target="_blank"
            >
              <img
                src="https://lemonprotector.com/images/home/blue-seal-63-134-lemon-protector-8459.png"
                alt="BBB Accredited"
                className="h-8 object-contain"
              />
            </a>
            <span className="w-px h-4 bg-brand-gold/20" />
            <span className="font-sans text-xs text-brand-gray">25+ Years in Business</span>
          </div>
        </div>

        {/* Stats strip */}
        <div ref={statsRef} className="lp-reveal mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((stat, i) => (
            <div
              key={i}
              className={`flex items-center gap-4 py-4 px-5 rounded-2xl bg-brand-black/50 backdrop-blur-sm border border-brand-gold/12 ${i < 3 ? "lg:border-r-0 lg:rounded-r-none" : ""}`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-gold/12 shrink-0">
                <i className={`${stat.icon} text-brand-gold text-xl`} />
              </div>
              <div>
                <p className="font-serif text-2xl font-bold text-brand-gold leading-none">
                  {stat.value}<span className="text-lg">{stat.suffix}</span>
                </p>
                <p className="font-sans text-xs text-brand-gray mt-0.5 whitespace-nowrap">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none">
        <span className="font-sans text-xs tracking-widest uppercase text-brand-gold/40">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-brand-gold/35 to-transparent" />
      </div>
    </section>
  );
}
